import { type Combobox } from '@base-ui/react/combobox';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildPartialSelectionMap, getNextSelectedValues } from '../utils/selection/index';
import { normalizeParentSelection } from '../utils/tree';
import type { DropdownValue, FlatItem, InitialSelectionMode } from '../types';

type UseDropdownSelectionResult = {
  selectedValues: DropdownValue[];
  selectedSet: Set<DropdownValue>;
  allSelected: boolean;
  partiallySelected: boolean;
  partialByValue: Map<DropdownValue, boolean>;
  handleSelectionChange: (
    nextValues: DropdownValue[] | DropdownValue | null,
    event?: Combobox.Root.ChangeEventDetails,
  ) => DropdownValue[];
  toggleSelectionForValue: (value: DropdownValue) => DropdownValue[];
  toggleAllSelections: () => DropdownValue[];
};

type UseDropdownSelectionOptions = {
  multiple: boolean;
  allItems: FlatItem[];
  allValues: DropdownValue[];
  itemsByDepthDesc: FlatItem[];
  selectableByValue: Map<DropdownValue, boolean>;
  descendantsByValue: Map<DropdownValue, DropdownValue[]>;
  selectedValues?: DropdownValue[];
  initialSelectedValues?: DropdownValue[];
  initialSelectionMode?: InitialSelectionMode;
  onSelectionChange?: (
    nextSelectedValues: DropdownValue[],
    event?: Combobox.Root.ChangeEventDetails,
  ) => void;
};

function getMatchingSelectedValues({
  allValues,
  multiple,
  selectedValues,
}: {
  allValues: DropdownValue[];
  multiple: boolean;
  selectedValues?: DropdownValue[];
}): DropdownValue[] | undefined {
  if (selectedValues === undefined) {
    return undefined;
  }

  if (selectedValues.length === 0) {
    return [];
  }

  const requestedValueSet = new Set(selectedValues);
  const matchingValues = allValues.filter((value) => requestedValueSet.has(value));

  if (matchingValues.length > 0) {
    return multiple ? matchingValues : [matchingValues[0]];
  }

  return undefined;
}

function getInitialSelection({
  allValues,
  multiple,
  initialSelectedValues,
  initialSelectionMode,
}: {
  allValues: DropdownValue[];
  multiple: boolean;
  initialSelectedValues?: DropdownValue[];
  initialSelectionMode?: InitialSelectionMode;
}): DropdownValue[] | undefined {
  if (initialSelectedValues !== undefined) {
    return getMatchingSelectedValues({
      allValues,
      multiple,
      selectedValues: initialSelectedValues,
    });
  }

  if (initialSelectionMode === 'all') {
    if (!multiple) {
      return [];
    }

    if (allValues.length > 0) {
      return allValues;
    }

    return undefined;
  }

  if (initialSelectionMode === 'first') {
    if (allValues.length > 0) {
      return [allValues[0]];
    }

    return undefined;
  }

  return [];
}

export function useDropdownSelection({
  multiple,
  allItems,
  allValues,
  itemsByDepthDesc,
  selectableByValue,
  descendantsByValue,
  selectedValues: controlledSelectedValues,
  initialSelectedValues,
  initialSelectionMode,
  onSelectionChange,
}: UseDropdownSelectionOptions): UseDropdownSelectionResult {
  const isControlled = controlledSelectedValues !== undefined;
  const hasInitializedSelectionRef = useRef(
    initialSelectedValues === undefined && initialSelectionMode === undefined,
  );
  const [uncontrolledSelectedValues, setUncontrolledSelectedValues] = useState<DropdownValue[]>([]);
  const allValueSet = useMemo(() => new Set(allValues), [allValues]);
  const filterSelectedValues = useCallback(
    (values: DropdownValue[]) => values.filter((value) => allValueSet.has(value)),
    [allValueSet],
  );
  const controlledSelection = useMemo(() => {
    if (!isControlled) {
      return undefined;
    }

    return (
      getMatchingSelectedValues({
        allValues,
        multiple,
        selectedValues: controlledSelectedValues,
      }) ?? []
    );
  }, [allValues, controlledSelectedValues, isControlled, multiple]);
  const validSelectedValues = useMemo(
    () =>
      isControlled ? (controlledSelection ?? []) : filterSelectedValues(uncontrolledSelectedValues),
    [controlledSelection, filterSelectedValues, isControlled, uncontrolledSelectedValues],
  );
  const selectedSet = useMemo(() => new Set(validSelectedValues), [validSelectedValues]);
  const allSelected =
    multiple && allValues.length > 0 && allValues.every((value) => selectedSet.has(value));
  const partiallySelected = multiple && !allSelected && selectedSet.size > 0;

  const partialByValue = useMemo(() => {
    if (!multiple) {
      return new Map<DropdownValue, boolean>();
    }

    return buildPartialSelectionMap(allItems, descendantsByValue, selectableByValue, selectedSet);
  }, [allItems, descendantsByValue, multiple, selectableByValue, selectedSet]);

  const commitSelection = useCallback(
    (
      nextSelectedValues: DropdownValue[],
      event?: Combobox.Root.ChangeEventDetails,
    ): DropdownValue[] => {
      const filteredSelectedValues = filterSelectedValues(nextSelectedValues);

      if (!isControlled) {
        hasInitializedSelectionRef.current = true;
        setUncontrolledSelectedValues(filteredSelectedValues);
      }

      onSelectionChange?.(filteredSelectedValues, event);

      return filteredSelectedValues;
    },
    [filterSelectedValues, isControlled, onSelectionChange],
  );

  useEffect(() => {
    if (isControlled || hasInitializedSelectionRef.current || validSelectedValues.length > 0) {
      return;
    }

    const initialSelection = getInitialSelection({
      allValues,
      multiple,
      initialSelectedValues,
      initialSelectionMode,
    });

    if (initialSelection === undefined) {
      return;
    }

    if (initialSelection.length > 0) {
      commitSelection(initialSelection);

      return;
    }

    hasInitializedSelectionRef.current = true;
  }, [
    allValues,
    commitSelection,
    initialSelectedValues,
    initialSelectionMode,
    isControlled,
    multiple,
    validSelectedValues.length,
  ]);

  const handleSelectionChange = useCallback(
    (
      nextValueInput: DropdownValue[] | DropdownValue | null,
      event?: Combobox.Root.ChangeEventDetails,
    ) =>
      commitSelection(
        getNextSelectedValues({
          previousValues: validSelectedValues,
          nextValueInput,
          multiple,
          selectableByValue,
          descendantsByValue,
          itemsByDepthDesc,
        }),
        event,
      ),
    [
      commitSelection,
      descendantsByValue,
      itemsByDepthDesc,
      multiple,
      selectableByValue,
      validSelectedValues,
    ],
  );

  const getNextValuesForToggle = useCallback(
    (previousValues: DropdownValue[], value: DropdownValue): DropdownValue[] => {
      if (!multiple) {
        return previousValues;
      }

      const descendants = descendantsByValue.get(value) ?? [];
      const targetValues = selectableByValue.get(value) ? [value, ...descendants] : descendants;

      if (targetValues.length === 0) {
        return previousValues;
      }

      const nextSet = new Set(previousValues);
      const shouldClearSelection = targetValues.every((targetValue) => nextSet.has(targetValue));

      for (const targetValue of targetValues) {
        if (shouldClearSelection) {
          nextSet.delete(targetValue);
        } else {
          nextSet.add(targetValue);
        }
      }

      const normalized = normalizeParentSelection(
        nextSet,
        itemsByDepthDesc,
        descendantsByValue,
        selectableByValue,
      );

      return [...normalized];
    },
    [descendantsByValue, itemsByDepthDesc, multiple, selectableByValue],
  );

  const toggleSelectionForValue = useCallback(
    (value: DropdownValue) => commitSelection(getNextValuesForToggle(validSelectedValues, value)),
    [commitSelection, getNextValuesForToggle, validSelectedValues],
  );

  const toggleAllSelections = useCallback(() => {
    if (!multiple) {
      return validSelectedValues;
    }

    return commitSelection(allSelected ? [] : allValues);
  }, [allSelected, allValues, commitSelection, multiple, validSelectedValues]);

  return {
    selectedValues: validSelectedValues,
    selectedSet,
    allSelected,
    partiallySelected,
    partialByValue,
    handleSelectionChange,
    toggleSelectionForValue,
    toggleAllSelections,
  };
}
